import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Avatar,
    Box,
    Button,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Pagination,
    Paper,
    Select,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import { NavLink } from "react-router-dom";

import { toEmptyStringIfNull } from "../util/utilities";

import {
    addLeadNoteAPI,
    fetchLeadAPI,
} from "../api/lead_api";

import {
    SALE_MODE,
    fetchSaleTagsAPI,
    reauctionLeadAPI,
    resellLeadAPI,
} from "../api/sale_api";

import {
    abortOfferAPI,
    fetchOfferAPI,
} from "../api/offer_api";

import {
    abortAuctionAPI,
    fetchAuctionStatusAPI,
} from "../api/auction_api";

import { 
    getIncreasingPollingDelay,
    POLLING_SPEED,
    usePolling,
} from "../util/polling";

import {
    PRIMARY_COLOR,
    SECONDARY_COLOR
} from "./../util/constants"

import { fetchUserProfileAPI } from "../api/user_api";


const EMPTY_VALUE = "–";
const DIALOG_Z_INDEX = 6000;
const SELECT_MENU_Z_INDEX = DIALOG_Z_INDEX + 100;
const INTEGER_REGEX = /^[0-9]*$/;

const SELECT_MENU_PROPS = Object.freeze({
    sx: {
        zIndex: SELECT_MENU_Z_INDEX,
    },
    PaperProps: {
        sx: {
            maxHeight: 360,
        },
    },
});

const LEAD_STATUS = Object.freeze({
    created: "CREATED",
    forSale: "FOR_SALE",
    notPaidSaleReversed: "NOT_PAID_SALE_REVERSED",
    soldPaid: "SOLD_PAID",
    soldNotPaid: "SOLD_NOT_PAID",
    notSold: "NOT_SOLD",
    complained: "COMPLAINED",
    invalidated: "INVALIDATED",
});

const SALE_STATUS = Object.freeze({
    created: "CREATED",
    active: "ACTIVE",
    terminated: "TERMINATED",
    aborted: "ABORTED",
});

const ACTION = Object.freeze({
    addNote: "addNote",
    abortOffer: "abortOffer",
    abortAuction: "abortAuction",
    resell: "resell",
});

const CONFIG = Object.freeze({
    minimumPrice: 1,
    notesPerPage: 5,
    synchronizationIntervalMs: 400,
    synchronizationAttempts: 15,
    finalTwoMinutesMs: 120_000,
    finalThirtySecondsMs: 30_000,
});

const LABEL = Object.freeze({
    details: "Details",
    currentSale: "Aktueller Verkauf",
    documents: "Dokumente",
    counterpartyProfile: "Profil der Gegenpartei",
    notes: "Notizen",
    payment: "Zahlung",
    checkout: "Checkout",
    startCheckout: "Checkout starten",
    paymentDeadline: "Zahlbar bis",

    lead: "Lead",
    person: "Person",
    contact: "Kontakt",
    address: "Adresse",
    comments: "Kommentare",

    publicComment: "Öffentlicher Kommentar",
    privateComment: "Interner Kommentar",

    id: "ID",
    type: "Art",
    status: "Status",
    category: "Kategorie",
    creationDate: "Erstellt am",

    businessName: "Firma",
    firstName: "Vorname",
    lastName: "Nachname",
    birthDate: "Geburtsdatum",
    phoneNumber: "Telefonnummer",
    emailAddress: "E-Mail-Adresse",
    street: "Straße",
    houseNo: "Hausnummer",
    zipCode: "Postleitzahl",
    town: "Ort",
    country: "Land",

    agreedPrice: "Verkaufspreis",
    offeredAt: "Angeboten am",
    approvedAt: "Angenommen am",

    offer: "Sofortkauf",
    auction: "Auktion",
    offerPrice: "Sofortkaufpreis",
    minimumBid: "Mindestgebot",
    priceInEuro: "Preis in €",
    minimumBidInEuro: "Mindestgebot in €",
    highestBid: "Aktuelles Höchstgebot",
    noBid: "Noch kein Gebot",
    highestBidder: "Sie sind Höchstbietender",

    start: "Start",
    end: "Ende",
    remainingTime: "Verbleibende Zeit",
    purchaseDate: "Kaufzeitpunkt",
    visibility: "Sichtbarkeit",
    public: "Öffentlich",
    private: "Privat",
    publicSale: "Öffentlicher Verkauf",
    tags: "Partnergruppen",
    tagsHelp:
        "Ohne Partnergruppe ist der Verkauf öffentlich. Mit Partnergruppen wird er privat.",
    noTags: "Keine Partnergruppen verfügbar",

    termsAndPrivacy: "AGB und Datenschutz",
    sellerTerms: "Verkäufer-AGB",
    sellerPrivacyPolicy: "Datenschutzerklärung des Verkäufers",
    operatorTerms: "Betreiber-AGB",
    operatorPrivacyPolicy: "Datenschutzerklärung des Betreibers",
    pdfDocument: "PDF-Dokument",
    download: "Herunterladen",
    invoices: "Rechnungen",
    invoiceOverview: "Rechnungsübersicht",
    show: "Anzeigen",

    abortOffer: "Sofortkauf abbrechen",
    abortAuction: "Auktion abbrechen",
    resell: "Erneut verkaufen",
    mode: "Verkaufsmodus",
    createOffer: "Sofortkauf anlegen",
    createAuction: "Auktion anlegen",

    note: "Notiz",
    addNote: "Notiz anlegen",

    sellerProfile: "Verkäuferprofil",
    buyerProfile: "Käuferprofil",
    company: "Unternehmen",
    seller: "Verkäufer",
    buyer: "Käufer",

    close: "Schließen",
    confirm: "Bestätigen",
});

const MESSAGE = Object.freeze({
    accessTokenMissing: "Für den Zugriff fehlt ein gültiger Access Token.",
    loadingError: "Der Lead konnte nicht geladen werden.",
    tagsError: "Die Partnergruppen konnten nicht geladen werden.",
    inconsistentSale:
        "Der Lead enthält gleichzeitig einen Sofortkauf und eine Auktion.",

    auctionStatusError: "Der Auktionsstatus konnte nicht aktualisiert werden.",
    offerStatusError: "Der Sofortkaufstatus konnte nicht aktualisiert werden.",
    synchronizationError:
        "Die Änderung wurde gespeichert, der Lead konnte aber noch nicht vollständig synchronisiert werden.",

    noteSaved: "Die Notiz wurde gespeichert.",
    noteError: "Die Notiz konnte nicht gespeichert werden.",
    noNotes: "Keine Notizen vorhanden.",

    noCurrentSale: "Für diesen Lead ist kein Verkauf hinterlegt.",
    noComment: "Nicht vorhanden",
    invalidPrice: "Bitte einen ganzen Eurobetrag ab 1 EUR eingeben.",

    offerCreated: "Der Sofortkauf wurde angelegt.",
    auctionCreated: "Die Auktion wurde angelegt.",
    offerCreateError: "Der Sofortkauf konnte nicht angelegt werden.",
    auctionCreateError: "Die Auktion konnte nicht angelegt werden.",

    offerAborted: "Der Sofortkauf wurde abgebrochen.",
    auctionAborted: "Die Auktion wurde abgebrochen.",
    offerAbortError: "Der Sofortkauf konnte nicht abgebrochen werden.",
    auctionAbortError: "Die Auktion konnte nicht abgebrochen werden.",
    auctionAbortBidExists:
        "Die Auktion kann nicht mehr abgebrochen werden, da bereits ein Gebot vorliegt.",

    abortOfferQuestion: "Möchten Sie den laufenden Sofortkauf wirklich abbrechen?",
    abortAuctionQuestion: "Möchten Sie die laufende Auktion wirklich abbrechen?",

    profileLoading: "Profil wird geladen …",
    profileError: "Das öffentliche Profil konnte nicht geladen werden.",
    profileUnavailable: "Für die Gegenpartei ist noch kein Profil verfügbar.",

    documentMissing: "Nicht hinterlegt",
    documentUnavailable: "Das Dokument ist nicht verfügbar.",
    documentDownloadFailed: "Das Dokument konnte nicht heruntergeladen werden.",
    invoiceUnavailable: "Noch nicht verfügbar",

    checkoutRequired:
        "Schließen Sie die Zahlung ab, um die exklusiven Lead-Informationen freizuschalten.",
    checkoutUnavailable: "Der Checkout ist derzeit nicht verfügbar.",
    reversedAccessDenied:
        "Der Zugriff auf diesen Lead ist nach der Rückabwicklung nur noch für den Verkäufer möglich.",
});

const FILE_NAME = Object.freeze({
    sellerTerms: "Verkaeufer-AGB.pdf",
    sellerPrivacyPolicy: "Verkaeufer-Datenschutzerklaerung.pdf",
    operatorTerms: "Betreiber-AGB.pdf",
    operatorPrivacyPolicy: "Betreiber-Datenschutzerklaerung.pdf",
});

const STATUS_COLOR = Object.freeze({
    notSoldText: "rgb(183, 28, 28)",
    notSoldBackground: "rgb(255, 235, 238)",
    notSoldBorder: "rgb(239, 154, 154)",
    complainedText: "rgb(230, 81, 0)",
    complainedBackground: "rgb(255, 243, 224)",
    complainedBorder: "rgb(255, 183, 77)",
    invalidatedText: "rgb(66, 66, 66)",
    invalidatedBackground: "rgb(238, 238, 238)",
    invalidatedBorder: "rgb(189, 189, 189)",
    neutralText: "white",
    neutralBackground: "rgba(255, 255, 255, 0.18)",
    neutralBorder: "rgba(255, 255, 255, 0.38)",
});

const SALE_MODE_COLOR = Object.freeze({
    auctionText: "rgb(21, 101, 192)",
    auctionBackground: "rgb(227, 242, 253)",
    auctionBorder: "rgb(144, 202, 249)",
    offerText: "rgb(46, 125, 50)",
    offerBackground: "rgb(232, 245, 233)",
    offerBorder: "rgb(165, 214, 167)",
});

const STYLE = Object.freeze({
    page: {
        width: "100%",
        mt: {
            xs: 2,
            sm: 3,
        },
        fontFamily: (theme) => theme.typography.fontFamily,
        "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-label, & .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root": {
            fontFamily: "inherit",
        },
    },
    headerPaper: {
        display: "block",
        width: "100%",
        overflow: "hidden",
        borderRadius: 3,
        boxShadow: `0 16px 42px ${alpha(PRIMARY_COLOR, 0.2)}`,
    },
    headerBar: {
        minHeight: "148px",
        padding: {
            xs: "24px",
            sm: "30px",
        },
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
        color: "white",
        backgroundColor: PRIMARY_COLOR,
    },
    headerContent: {
        width: "100%",
    },
    headerAvatar: {
        width: {
            xs: 72,
            sm: 84,
        },
        height: {
            xs: 72,
            sm: 84,
        },
        flexShrink: 0,
        color: "white",
        backgroundColor: "rgba(255,255,255,0.20)",
        border: "2px solid rgba(255,255,255,0.30)",
        "& .MuiSvgIcon-root": {
            fontSize: {
                xs: 34,
                sm: 40,
            },
        },
    },
    headerTitle: {
        fontFamily: "inherit",
        fontSize: {
            xs: "1.55rem",
            sm: "1.9rem",
        },
        fontWeight: 700,
        lineHeight: 1.2,
        overflowWrap: "anywhere",
    },
    headerSubtitle: {
        fontFamily: "inherit",
        marginTop: "4px",
        opacity: 0.86,
        overflowWrap: "anywhere",
    },
    headerChip: {
        fontFamily: "inherit",
        color: "white",
        backgroundColor: "rgba(255,255,255,0.16)",
        border: "1px solid rgba(255,255,255,0.24)",
        fontWeight: 700,
        "& .MuiChip-icon": {
            color: "white",
        },
    },
    badgeContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        flexWrap: "wrap",
        flexShrink: 0,
    },
    section: {
        display: "block",
    },
    sectionTitle: {
        display: "block",
        marginBottom: "12px",
        color: "text.secondary",
        fontFamily: "inherit",
        fontSize: "0.75rem",
        fontWeight: 700,
        lineHeight: 1.5,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
    },
    card: {
        display: "block",
        height: "100%",
        overflow: "hidden",
        borderRadius: 3,
        borderColor: alpha(PRIMARY_COLOR, 0.14),
        boxShadow: `0 12px 30px ${alpha(PRIMARY_COLOR, 0.06)}`,
    },
    cardContent: {
        padding: {
            xs: "18px",
            sm: "24px",
        },
        "&:last-child": {
            paddingBottom: {
                xs: "18px",
                sm: "24px",
            },
        },
    },
    cardHeader: {
        marginBottom: "18px",
    },
    iconBox: {
        width: 34,
        height: 34,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        borderRadius: 2,
        color: PRIMARY_COLOR,
        backgroundColor: alpha(PRIMARY_COLOR, 0.09),
    },
    cardTitle: {
        fontFamily: "inherit",
        fontSize: "1rem",
        fontWeight: 700,
        overflowWrap: "anywhere",
    },
    infoLabel: {
        display: "block",
        fontFamily: "inherit",
        marginBottom: "3px",
        fontWeight: 600,
    },
    infoValue: {
        fontFamily: "inherit",
        overflowWrap: "anywhere",
        lineHeight: 1.45,
    },
    documentRow: {
        paddingTop: "10px",
        paddingBottom: "10px",
    },
    documentButton: {
        alignSelf: {
            xs: "stretch",
            sm: "center",
        },
        borderRadius: 2,
    },
});

const sleep = (milliseconds) => (
    new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
    })
);

const idsAreEqual = (firstId, secondId) => {
    if (
        firstId === null
        || firstId === undefined
        || secondId === null
        || secondId === undefined
    ) {
        return false;
    }

    return String(firstId) === String(secondId);
};

const hasValue = (value) => (
    value !== null
    && value !== undefined
    && value !== ""
);

const displayValue = (value) => (
    hasValue(value) ? String(value) : EMPTY_VALUE
);

const formatDateTime = (value) => {
    if (!hasValue(value)) {
        return EMPTY_VALUE;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return new Intl.DateTimeFormat("de-DE", {
        dateStyle: "medium",
        timeStyle: "medium",
    }).format(date);
};

const formatDate = (value) => {
    if (!hasValue(value)) {
        return EMPTY_VALUE;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return new Intl.DateTimeFormat("de-DE", {
        dateStyle: "medium",
    }).format(date);
};

const formatCurrency = (value) => {
    if (!hasValue(value)) {
        return EMPTY_VALUE;
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return EMPTY_VALUE;
    }

    return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(numericValue);
};

const formatDuration = (duration) => {
    const milliseconds = Number(duration);

    if (!Number.isFinite(milliseconds)) {
        return EMPTY_VALUE;
    }

    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const days = Math.floor(totalSeconds / 86_400);
    const hours = Math.floor((totalSeconds % 86_400) / 3_600);
    const minutes = Math.floor((totalSeconds % 3_600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];

    if (days > 0) {
        parts.push(`${days} T`);
    }

    if (hours > 0 || days > 0) {
        parts.push(`${hours} Std`);
    }

    if (minutes > 0 || hours > 0 || days > 0) {
        parts.push(`${minutes} Min`);
    }

    if (days === 0 && hours === 0) {
        parts.push(`${seconds} Sek`);
    }

    return parts.join(" ");
};

const getSaleEndTimestamp = (sale) => {
    const startTimestamp = Number(sale?.startTimestamp);
    const duration = Number(sale?.duration);

    if (!Number.isFinite(startTimestamp) || !Number.isFinite(duration)) {
        return null;
    }

    return startTimestamp + duration;
};

const formatRemainingTime = (endTimestamp, now = Date.now()) => {
    const remaining = Number(endTimestamp) - Number(now);

    if (!Number.isFinite(remaining)) {
        return EMPTY_VALUE;
    }

    if (remaining <= 0) {
        return "Beendet";
    }

    return formatDuration(remaining);
};

const calculateProgress = (startTimestamp, duration, now = Date.now()) => {
    const start = Number(startTimestamp);
    const length = Number(duration);

    if (!Number.isFinite(start) || !Number.isFinite(length) || length <= 0) {
        return 0;
    }

    return Math.min(
        100,
        Math.max(0, ((Number(now) - start) / length) * 100),
    );
};

const getSalePollingDelay = ({
    endTimestamp,
    pollingStartedAt,
}) => getIncreasingPollingDelay({
    endTimestamp,
    mediumThresholdMs: CONFIG.finalTwoMinutesMs,
    fastThresholdMs: CONFIG.finalThirtySecondsMs,
    pollingStartedAt,
});

const getCategoryName = (lead) => (
    lead?.category?.name ?? EMPTY_VALUE
);

const getLeadTypeLabel = (type) => {
    if (type === "B2B") {
        return "Geschäftskunde";
    }

    if (type === "B2C") {
        return "Privatkunde";
    }

    return displayValue(type);
};

const getSaleStatusLabel = (status) => {
    const labels = {
        [SALE_STATUS.created]: "Geplant",
        [SALE_STATUS.active]: "Läuft",
        [SALE_STATUS.terminated]: "Beendet",
        [SALE_STATUS.aborted]: "Abgebrochen",
    };

    return labels[status] ?? displayValue(status);
};

const getCurrentSale = (lead) => {
    const hasOffer = lead?.recentOffer !== null
        && lead?.recentOffer !== undefined;
    const hasAuction = lead?.recentAuction !== null
        && lead?.recentAuction !== undefined;

    if (hasOffer && hasAuction) {
        return {
            invalid: true,
            mode: null,
            sale: null,
        };
    }

    if (hasOffer) {
        return {
            invalid: false,
            mode: SALE_MODE.OFFER,
            sale: lead.recentOffer,
        };
    }

    if (hasAuction) {
        return {
            invalid: false,
            mode: SALE_MODE.AUCTION,
            sale: lead.recentAuction,
        };
    }

    return {
        invalid: false,
        mode: null,
        sale: null,
    };
};

const getLatestApprovedPriceAgreement = (agreements) => {
    if (!Array.isArray(agreements)) {
        return null;
    }

    return agreements
        .filter((agreement) => (
            agreement?.approvalTimestamp !== null
            && agreement?.approvalTimestamp !== undefined
        ))
        .reduce((latest, agreement) => {
            if (!latest) {
                return agreement;
            }

            const currentTimestamp = Number(agreement?.offerTimestamp ?? 0);
            const latestTimestamp = Number(latest?.offerTimestamp ?? 0);

            if (currentTimestamp !== latestTimestamp) {
                return currentTimestamp > latestTimestamp
                    ? agreement
                    : latest;
            }

            return Number(agreement?.approvalTimestamp ?? 0)
                > Number(latest?.approvalTimestamp ?? 0)
                ? agreement
                : latest;
        }, null);
};

const isOpenSaleStatus = (status) => [
    SALE_STATUS.created,
    SALE_STATUS.active,
].includes(status);

const isTerminalSaleStatus = (status) => [
    SALE_STATUS.terminated,
    SALE_STATUS.aborted,
].includes(status);

const isSaleRunning = (sale, now = Date.now()) => {
    if (!sale || !isOpenSaleStatus(sale.status)) {
        return false;
    }

    const endTimestamp = getSaleEndTimestamp(sale);

    return endTimestamp === null || endTimestamp > Number(now);
};

const getSaleModeConfig = (saleMode) => {
    if (saleMode === SALE_MODE.AUCTION) {
        return {
            label: LABEL.auction,
            color: SALE_MODE_COLOR.auctionText,
            backgroundColor: SALE_MODE_COLOR.auctionBackground,
            borderColor: SALE_MODE_COLOR.auctionBorder,
        };
    }

    if (saleMode === SALE_MODE.OFFER) {
        return {
            label: LABEL.offer,
            color: SALE_MODE_COLOR.offerText,
            backgroundColor: SALE_MODE_COLOR.offerBackground,
            borderColor: SALE_MODE_COLOR.offerBorder,
        };
    }

    return null;
};

const getStatusConfig = (status, isAuthor, isBuyer) => {
    switch (status) {
        case LEAD_STATUS.created:
            return {
                label: "Erstellt",
                color: STATUS_COLOR.neutralText,
                backgroundColor: STATUS_COLOR.neutralBackground,
                borderColor: STATUS_COLOR.neutralBorder,
            };
        case LEAD_STATUS.forSale:
            return {
                label: "Im Verkauf",
                color: "white",
                backgroundColor: SECONDARY_COLOR,
                borderColor: SECONDARY_COLOR,
            };
        case LEAD_STATUS.soldNotPaid:
            return {
                label: isBuyer && !isAuthor
                    ? "Gekauft – Zahlung offen"
                    : "Verkauft – Zahlung offen",
                color: STATUS_COLOR.neutralText,
                backgroundColor: STATUS_COLOR.neutralBackground,
                borderColor: STATUS_COLOR.neutralBorder,
            };
        case LEAD_STATUS.soldPaid:
            return {
                label: isBuyer && !isAuthor
                    ? "Gekauft – bezahlt"
                    : "Verkauft – bezahlt",
                color: STATUS_COLOR.neutralText,
                backgroundColor: STATUS_COLOR.neutralBackground,
                borderColor: STATUS_COLOR.neutralBorder,
            };
        case LEAD_STATUS.notPaidSaleReversed:
            return {
                label: "Nicht bezahlt – Verkauf rückabgewickelt",
                color: STATUS_COLOR.notSoldText,
                backgroundColor: STATUS_COLOR.notSoldBackground,
                borderColor: STATUS_COLOR.notSoldBorder,
            };
        case LEAD_STATUS.notSold:
            return {
                label: "Nicht verkauft",
                color: STATUS_COLOR.notSoldText,
                backgroundColor: STATUS_COLOR.notSoldBackground,
                borderColor: STATUS_COLOR.notSoldBorder,
            };
        case LEAD_STATUS.complained:
            return {
                label: "Beanstandet",
                color: STATUS_COLOR.complainedText,
                backgroundColor: STATUS_COLOR.complainedBackground,
                borderColor: STATUS_COLOR.complainedBorder,
            };
        case LEAD_STATUS.invalidated:
            return {
                label: "Ungültig",
                color: STATUS_COLOR.invalidatedText,
                backgroundColor: STATUS_COLOR.invalidatedBackground,
                borderColor: STATUS_COLOR.invalidatedBorder,
            };
        default:
            return null;
    }
};

const isResellableLeadStatus = (status) => (
    status === LEAD_STATUS.notSold
);

const getLeadHeading = (lead) => {
    const firstName = toEmptyStringIfNull(lead?.firstName).trim();
    const lastName = toEmptyStringIfNull(lead?.lastName).trim();
    const personName = `${firstName} ${lastName}`.trim();

    if (lead?.type === "B2B" && lead?.businessName) {
        return lead.businessName;
    }

    return personName || lead?.businessName || `Lead #${lead?.id ?? ""}`;
};

const hasBinaryData = (value) => {
    if (typeof value === "string") {
        return value.trim().length > 0;
    }

    if (value instanceof Uint8Array) {
        return value.length > 0;
    }

    return Array.isArray(value) && value.length > 0;
};

const base64ToBytes = (value) => {
    if (value instanceof Uint8Array) {
        return value;
    }

    if (Array.isArray(value)) {
        return Uint8Array.from(value);
    }

    if (typeof value !== "string" || value.trim() === "") {
        return null;
    }

    let base64 = value.includes(",")
        ? value.slice(value.indexOf(",") + 1)
        : value;

    base64 = base64
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    base64 += "=".repeat((4 - base64.length % 4) % 4);

    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
};

const downloadPdf = (value, fileName) => {
    const bytes = base64ToBytes(value);

    if (bytes === null || bytes.length === 0) {
        throw new Error(MESSAGE.documentUnavailable);
    }

    const blob = new Blob([bytes], {
        type: "application/pdf",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
    }, 0);
};

const getTagName = (tagId, availableTags) => {
    const tag = availableTags.find((item) => idsAreEqual(item?.id, tagId));
    return tag?.name ?? `Partnergruppe ${tagId}`;
};

const normalizeTagIds = (tagIds) => {
    if (!Array.isArray(tagIds)) {
        return [];
    }

    return tagIds
        .map(Number)
        .filter(Number.isSafeInteger);
};

const extractPayloadMessage = (payload) => {
    if (typeof payload === "string") {
        return payload.trim();
    }

    if (!payload || typeof payload !== "object") {
        return "";
    }

    const directMessage = [
        payload.message,
        payload.detail,
        payload.error,
        payload.title,
    ].find((value) => typeof value === "string" && value.trim() !== "");

    if (directMessage) {
        return directMessage.trim();
    }

    try {
        return JSON.stringify(payload);
    } catch {
        return "";
    }
};

async function readResponsePayload(request, errorMessage) {
    const response = await request;

    if (
        !response
        || typeof response.text !== "function"
        || typeof response.ok !== "boolean"
    ) {
        return response;
    }

    const responseText = await response.text();
    let payload = null;

    if (responseText !== "") {
        try {
            payload = JSON.parse(responseText);
        } catch {
            payload = responseText;
        }
    }

    if (!response.ok) {
        const details = extractPayloadMessage(payload);
        throw new Error(
            `${errorMessage} HTTP ${response.status}`
            + (details ? `: ${details}` : ""),
        );
    }

    return payload;
}

function HeaderBadge({ children, icon = null }) {
    return (
        <Chip
            icon={icon}
            label={children}
            size="small"
            sx={STYLE.headerChip}
        />
    );
}

function SectionHeading({ children }) {
    return (
        <Typography
            component="h2"
            sx={STYLE.sectionTitle}
        >
            {children}
        </Typography>
    );
}

function OverviewCard({ title, icon, action = null, children, sx }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                ...STYLE.card,
                ...sx,
            }}
        >
            <CardContent sx={STYLE.cardContent}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={STYLE.cardHeader}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                        sx={{ minWidth: 0 }}
                    >
                        <Box sx={STYLE.iconBox}>{icon}</Box>
                        <Typography
                            component="h3"
                            variant="subtitle1"
                            sx={STYLE.cardTitle}
                        >
                            {title}
                        </Typography>
                    </Stack>
                    {action}
                </Stack>

                {children}
            </CardContent>
        </Paper>
    );
}

function InfoRow({ label, value, monospace = false }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={STYLE.infoLabel}
            >
                {label}
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    ...STYLE.infoValue,
                    fontFamily: monospace ? "monospace" : "inherit",
                }}
            >
                {displayValue(value)}
            </Typography>
        </Box>
    );
}

function TagChips({ tagIds, availableTags }) {
    const normalizedTagIds = normalizeTagIds(tagIds);

    if (normalizedTagIds.length === 0) {
        return (
            <Chip
                size="small"
                label={LABEL.public}
                color="success"
                variant="outlined"
            />
        );
    }

    return (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip
                size="small"
                label={LABEL.private}
                color="warning"
                variant="outlined"
            />
            {normalizedTagIds.map((tagId) => (
                <Chip
                    key={tagId}
                    size="small"
                    label={getTagName(tagId, availableTags)}
                />
            ))}
        </Stack>
    );
}

function PriceAgreementHighlight({ agreement }) {
    if (!agreement) {
        return null;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                overflow: "hidden",
                borderColor: alpha(PRIMARY_COLOR, 0.14),
                borderRadius: 3,
                boxShadow: `0 12px 30px ${alpha(PRIMARY_COLOR, 0.06)}`,
                backgroundColor: "background.paper",
            }}
        >
            <Grid container alignItems="stretch">
                <Grid item xs={12} md={5} lg={4}>
                    <Box
                        sx={{
                            height: "100%",
                            padding: {
                                xs: "24px",
                                sm: "30px",
                            },
                            color: "white",
                            backgroundColor: PRIMARY_COLOR,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    opacity: 0.9,
                                }}
                            >
                                {LABEL.agreedPrice}
                            </Typography>
                        </Stack>
                        <Typography
                            component="div"
                            sx={{
                                mt: 1,
                                fontSize: {
                                    xs: "2rem",
                                    sm: "2.5rem",
                                },
                                fontWeight: 800,
                                lineHeight: 1.1,
                            }}
                        >
                            {formatCurrency(agreement.price)}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} md={7} lg={8}>
                    <Grid
                        container
                        spacing={2.5}
                        sx={{
                            padding: {
                                xs: "24px",
                                sm: "30px",
                            },
                        }}
                    >
                        <Grid item xs={12} sm={6}>
                            <InfoRow
                                label={LABEL.offeredAt}
                                value={formatDateTime(agreement.offerTimestamp)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow
                                label={LABEL.approvedAt}
                                value={formatDateTime(agreement.approvalTimestamp)}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
}

function DocumentRow({ title, available, onDownload, actionLabel = LABEL.download }) {
    return (
        <Stack
            direction={{
                xs: "column",
                sm: "row",
            }}
            alignItems={{
                xs: "stretch",
                sm: "center",
            }}
            justifyContent="space-between"
            spacing={2}
            sx={STYLE.documentRow}
        >
            <Box>
                <Typography fontWeight={700}>{title}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {available ? LABEL.pdfDocument : MESSAGE.documentMissing}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                disabled={!available}
                onClick={onDownload}
                sx={STYLE.documentButton}
            >
                {actionLabel}
            </Button>
        </Stack>
    );
}

function InvoiceRow({ available, leadId }) {
    return (
        <Stack
            direction={{
                xs: "column",
                sm: "row",
            }}
            alignItems={{
                xs: "stretch",
                sm: "center",
            }}
            justifyContent="space-between"
            spacing={2}
            sx={STYLE.documentRow}
        >
            <Box>
                <Typography fontWeight={700}>{LABEL.invoiceOverview}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {available ? LABEL.invoices : MESSAGE.invoiceUnavailable}
                </Typography>
            </Box>
            <Button
                component={available ? NavLink : "button"}
                to={available ? `/invoicing/${leadId}` : undefined}
                variant="outlined"
                startIcon={<ReceiptLongOutlinedIcon />}
                disabled={!available}
                sx={STYLE.documentButton}
            >
                {LABEL.show}
            </Button>
        </Stack>
    );
}

function DocumentsSection({ sale, agreement, leadId, canViewInvoices, onDownload }) {
    if (!sale) {
        return null;
    }

    const invoiceAvailable = Boolean(
        canViewInvoices
        && agreement
        && (
            agreement.productInvoiceId !== null
            || agreement.transactionInvoiceId !== null
        ),
    );

    const documents = [
        {
            key: "sellerTerms",
            title: LABEL.sellerTerms,
            value: sale.sellerTerms,
            fileName: FILE_NAME.sellerTerms,
        },
        {
            key: "sellerPrivacyPolicy",
            title: LABEL.sellerPrivacyPolicy,
            value: sale.sellerPrivacyPolicy,
            fileName: FILE_NAME.sellerPrivacyPolicy,
        },
        {
            key: "operatorTerms",
            title: LABEL.operatorTerms,
            value: sale.operatorTerms,
            fileName: FILE_NAME.operatorTerms,
        },
        {
            key: "operatorPrivacyPolicy",
            title: LABEL.operatorPrivacyPolicy,
            value: sale.operatorPrivacyPolicy,
            fileName: FILE_NAME.operatorPrivacyPolicy,
        },
    ];

    return (
        <Box component="section" sx={STYLE.section}>
            <SectionHeading>{LABEL.documents}</SectionHeading>
            <OverviewCard
                title={LABEL.termsAndPrivacy}
                icon={<DescriptionOutlinedIcon fontSize="small" />}
            >
                {documents.map((document, index) => (
                    <Box key={document.key}>
                        {index > 0 && <Divider />}
                        <DocumentRow
                            title={document.title}
                            available={hasBinaryData(document.value)}
                            onDownload={() => {
                                onDownload(document.value, document.fileName);
                            }}
                        />
                    </Box>
                ))}
                <Divider />
                <InvoiceRow
                    available={invoiceAvailable}
                    leadId={leadId}
                />
            </OverviewCard>
        </Box>
    );
}

function CurrentSaleCard({
    lead,
    saleMode,
    sale,
    auctionStatus,
    saleNow,
    availableTags,
    isAuthor,
    busyAction,
    onAbortOffer,
    onAbortAuction,
}) {
    if (!sale) {
        return (
            <OverviewCard
                title={LABEL.currentSale}
                icon={<SellOutlinedIcon fontSize="small" />}
            >
                <Typography color="text.secondary">
                    {MESSAGE.noCurrentSale}
                </Typography>
            </OverviewCard>
        );
    }

    const isOffer = saleMode === SALE_MODE.OFFER;
    const endTimestamp = getSaleEndTimestamp(sale);
    const saleRunning = isSaleRunning(sale, saleNow);
    const showOfferOrMinimumPrice = saleRunning;
    const showCurrentHighestBid = !isOffer
        && sale.status === SALE_STATUS.active
        && idsAreEqual(auctionStatus?.auctionId, sale.id);
    const auctionStatusMatches = !isOffer
        && idsAreEqual(auctionStatus?.auctionId, sale.id);
    const auctionHasBid = auctionStatusMatches
        && hasValue(auctionStatus?.maxBid);
    const canAbort = isAuthor
        && lead?.status === LEAD_STATUS.forSale
        && saleRunning
        && (isOffer || (auctionStatusMatches && !auctionHasBid));

    const price = isOffer ? sale.price : sale.minBid;
    const validPrice = Number.isFinite(Number(price))
        && Number(price) >= CONFIG.minimumPrice
        ? Number(price)
        : null;

    return (
        <OverviewCard
            title={isOffer ? LABEL.offer : LABEL.auction}
            icon={isOffer
                ? <ShoppingCartOutlinedIcon fontSize="small" />
                : <GavelOutlinedIcon fontSize="small" />}
            action={canAbort ? (
                <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={busyAction !== null}
                    startIcon={busyAction === (
                        isOffer ? ACTION.abortOffer : ACTION.abortAuction
                    )
                        ? <CircularProgress size={16} color="inherit" />
                        : <CancelOutlinedIcon />}
                    onClick={isOffer ? onAbortOffer : onAbortAuction}
                >
                    {isOffer ? LABEL.abortOffer : LABEL.abortAuction}
                </Button>
            ) : null}
        >
            <Grid container spacing={3} alignItems="flex-start">
                {showOfferOrMinimumPrice && (
                    <Grid item xs={12} md={4} lg={3}>
                        <Box
                            sx={{
                                padding: "16px",
                                borderRadius: 2.5,
                                backgroundColor: alpha(PRIMARY_COLOR, 0.06),
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: "block",
                                    fontWeight: 700,
                                }}
                            >
                                {isOffer ? LABEL.offerPrice : LABEL.minimumBid}
                            </Typography>
                            <Typography
                                component="div"
                                sx={{
                                    mt: 0.5,
                                    color: PRIMARY_COLOR,
                                    fontSize: "2rem",
                                    fontWeight: 800,
                                    lineHeight: 1,
                                }}
                            >
                                {formatCurrency(validPrice)}
                            </Typography>

                            {showCurrentHighestBid && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: "block",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {LABEL.highestBid}
                                    </Typography>
                                    <Typography variant="h6" component="div" sx={{ mt: 0.25 }}>
                                        {auctionStatus?.maxBid === null
                                            || auctionStatus?.maxBid === undefined
                                            ? LABEL.noBid
                                            : formatCurrency(auctionStatus.maxBid)}
                                    </Typography>
                                    {Boolean(auctionStatus?.highestBidder) && (
                                        <Chip
                                            size="small"
                                            color="success"
                                            label={LABEL.highestBidder}
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Grid>
                )}

                <Grid
                    item
                    xs={12}
                    md={showOfferOrMinimumPrice ? 8 : 12}
                    lg={showOfferOrMinimumPrice ? 9 : 12}
                >
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <InfoRow
                                label={LABEL.status}
                                value={getSaleStatusLabel(sale.status)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <InfoRow
                                label={LABEL.start}
                                value={formatDateTime(sale.startTimestamp)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <InfoRow
                                label={LABEL.end}
                                value={formatDateTime(endTimestamp)}
                            />
                        </Grid>

                        {saleRunning && (
                            <Grid item xs={12} sm={6} lg={3}>
                                <InfoRow
                                    label={LABEL.remainingTime}
                                    value={formatRemainingTime(endTimestamp, saleNow)}
                                />
                            </Grid>
                        )}

                        {isOffer && hasValue(sale.purchaseTimestamp) && (
                            <Grid item xs={12} sm={6} lg={3}>
                                <InfoRow
                                    label={LABEL.purchaseDate}
                                    value={formatDateTime(sale.purchaseTimestamp)}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: "block",
                                    mb: 0.75,
                                    fontWeight: 600,
                                }}
                            >
                                {LABEL.visibility}
                            </Typography>
                            <TagChips
                                tagIds={sale.tagList}
                                availableTags={availableTags}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {saleRunning && endTimestamp !== null && (
                    <Grid item xs={12}>
                        <LinearProgress
                            variant="determinate"
                            value={calculateProgress(
                                sale.startTimestamp,
                                sale.duration,
                                saleNow,
                            )}
                            sx={{
                                height: 5,
                                borderRadius: 999,
                                backgroundColor: "action.hover",
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    backgroundColor: PRIMARY_COLOR,
                                },
                            }}
                        />
                    </Grid>
                )}
            </Grid>
        </OverviewCard>
    );
}

function ResellCard({ availableTags, busy, onCreate }) {
    const [mode, setMode] = useState(SALE_MODE.AUCTION);
    const [price, setPrice] = useState(String(CONFIG.minimumPrice));
    const [selectedTagIds, setSelectedTagIds] = useState([]);

    const validTags = useMemo(() => (
        availableTags.filter((tag) => (
            tag?.enabled !== false
            && tag?.id !== null
            && tag?.id !== undefined
        ))
    ), [availableTags]);

    const tagById = useMemo(() => (
        new Map(validTags.map((tag) => [String(tag.id), tag]))
    ), [validTags]);

    const normalizedPrice = Number(price);
    const priceIsValid = Number.isSafeInteger(normalizedPrice)
        && normalizedPrice >= CONFIG.minimumPrice;

    const renderTagSelection = (selectedIds) => {
        if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {LABEL.publicSale}
                </Typography>
            );
        }

        return (
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {selectedIds.map((tagId) => {
                    const normalizedTagId = String(tagId);

                    return (
                        <Chip
                            key={normalizedTagId}
                            size="small"
                            label={tagById.get(normalizedTagId)?.name
                                ?? normalizedTagId}
                        />
                    );
                })}
            </Box>
        );
    };

    const submit = (event) => {
        event.preventDefault();

        if (!priceIsValid || busy) {
            return;
        }

        onCreate({
            mode,
            price: normalizedPrice,
            tagIds: selectedTagIds
                .map(Number)
                .filter(Number.isSafeInteger),
        });
    };

    return (
        <OverviewCard
            title={LABEL.resell}
            icon={<ReplayOutlinedIcon fontSize="small" />}
        >
            <Box component="form" onSubmit={submit}>
                <Grid container spacing={3} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>{LABEL.mode}</InputLabel>
                            <Select
                                MenuProps={SELECT_MENU_PROPS}
                                value={mode}
                                label={LABEL.mode}
                                disabled={busy}
                                onChange={(event) => {
                                    setMode(event.target.value);
                                }}
                            >
                                <MenuItem value={SALE_MODE.OFFER}>
                                    {LABEL.offer}
                                </MenuItem>
                                <MenuItem value={SALE_MODE.AUCTION}>
                                    {LABEL.auction}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={mode === SALE_MODE.AUCTION
                                ? LABEL.minimumBidInEuro
                                : LABEL.priceInEuro}
                            value={price}
                            disabled={busy}
                            error={!priceIsValid}
                            helperText={priceIsValid ? " " : MESSAGE.invalidPrice}
                            inputProps={{ inputMode: "numeric" }}
                            onChange={(event) => {
                                const value = event.target.value;

                                if (INTEGER_REGEX.test(value)) {
                                    setPrice(value);
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth disabled={busy}>
                            <InputLabel>{LABEL.tags}</InputLabel>
                            <Select
                                MenuProps={SELECT_MENU_PROPS}
                                multiple
                                value={selectedTagIds}
                                label={LABEL.tags}
                                input={<OutlinedInput label={LABEL.tags} />}
                                renderValue={renderTagSelection}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSelectedTagIds(
                                        typeof value === "string"
                                            ? value.split(",")
                                            : value,
                                    );
                                }}
                            >
                                {validTags.length === 0 ? (
                                    <MenuItem disabled>
                                        <ListItemText primary={LABEL.noTags} />
                                    </MenuItem>
                                ) : validTags.map((tag) => {
                                    const tagId = String(tag.id);

                                    return (
                                        <MenuItem key={tagId} value={tagId}>
                                            <Checkbox
                                                checked={selectedTagIds.includes(tagId)}
                                            />
                                            <ListItemText primary={tag.name} />
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.75 }}
                            >
                                {LABEL.tagsHelp}
                            </Typography>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={busy || !priceIsValid}
                            startIcon={busy
                                ? <CircularProgress size={18} color="inherit" />
                                : <ReplayOutlinedIcon />}
                            sx={{
                                backgroundColor: SECONDARY_COLOR,
                                "&:hover": {
                                    backgroundColor: SECONDARY_COLOR,
                                    filter: "brightness(0.92)",
                                },
                            }}
                        >
                            {mode === SALE_MODE.OFFER
                                ? LABEL.createOffer
                                : LABEL.createAuction}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </OverviewCard>
    );
}

function NotesSection({
    notes,
    noteText,
    busy,
    page,
    onPageChange,
    onNoteTextChange,
    onSubmit,
}) {
    const sortedNotes = useMemo(() => (
        [...(Array.isArray(notes) ? notes : [])]
            .sort((first, second) => {
                const timestampDifference = Number(
                    second?.creationTimestamp ?? 0,
                ) - Number(first?.creationTimestamp ?? 0);

                if (timestampDifference !== 0) {
                    return timestampDifference;
                }

                return Number(second?.id ?? 0) - Number(first?.id ?? 0);
            })
    ), [notes]);

    const pageCount = Math.max(
        1,
        Math.ceil(sortedNotes.length / CONFIG.notesPerPage),
    );
    const safePage = Math.min(page, pageCount);
    const pageNotes = sortedNotes.slice(
        (safePage - 1) * CONFIG.notesPerPage,
        safePage * CONFIG.notesPerPage,
    );

    useEffect(() => {
        if (safePage !== page) {
            onPageChange(safePage);
        }
    }, [onPageChange, page, safePage]);

    return (
        <Box component="section" sx={STYLE.section}>
            <SectionHeading>{LABEL.notes}</SectionHeading>
            <OverviewCard
                title={LABEL.notes}
                icon={<NotesOutlinedIcon fontSize="small" />}
            >
                <Stack spacing={3}>
                    {pageNotes.length === 0 ? (
                        <Typography color="text.secondary">
                            {MESSAGE.noNotes}
                        </Typography>
                    ) : pageNotes.map((note) => (
                        <Paper
                            key={note?.id ?? `${note?.creationTimestamp}-${note?.note}`}
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {formatDateTime(note?.creationTimestamp)}
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.75,
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {displayValue(note?.note)}
                            </Typography>
                        </Paper>
                    ))}

                    {sortedNotes.length > CONFIG.notesPerPage && (
                        <Stack direction="row" justifyContent="center">
                            <Pagination
                                page={safePage}
                                count={pageCount}
                                color="primary"
                                onChange={(event, nextPage) => {
                                    onPageChange(nextPage);
                                }}
                            />
                        </Stack>
                    )}

                    <Divider />
                    <Box component="form" onSubmit={onSubmit}>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                value={noteText}
                                label={LABEL.note}
                                disabled={busy}
                                onChange={(event) => {
                                    onNoteTextChange(event.target.value);
                                }}
                            />
                            <Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={busy || noteText.trim() === ""}
                                    startIcon={busy
                                        ? <CircularProgress size={18} color="inherit" />
                                        : <NotesOutlinedIcon />}
                                    sx={{
                                        backgroundColor: SECONDARY_COLOR,
                                        "&:hover": {
                                            backgroundColor: SECONDARY_COLOR,
                                            filter: "brightness(0.92)",
                                        },
                                    }}
                                >
                                    {LABEL.addNote}
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>
            </OverviewCard>
        </Box>
    );
}

function CounterpartyProfileSection({ title, profile, loading, failed, available }) {
    const fullName = [
        profile?.firstName,
        profile?.lastName,
    ].filter(Boolean).join(" ");

    return (
        <Box component="section" sx={STYLE.section}>
            <SectionHeading>{LABEL.counterpartyProfile}</SectionHeading>
            <OverviewCard
                title={title ?? LABEL.counterpartyProfile}
                icon={<PersonOutlineIcon fontSize="small" />}
            >
                {!available && (
                    <Typography color="text.secondary">
                        {MESSAGE.profileUnavailable}
                    </Typography>
                )}

                {available && loading && (
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
                        <CircularProgress size={22} />
                        <Typography color="text.secondary">
                            {MESSAGE.profileLoading}
                        </Typography>
                    </Stack>
                )}

                {available && !loading && failed && (
                    <Alert severity="error">{MESSAGE.profileError}</Alert>
                )}

                {available && !loading && !failed && profile && (
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6} lg={4}>
                            <InfoRow label={LABEL.person} value={fullName} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <InfoRow
                                label={LABEL.company}
                                value={profile.businessName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <InfoRow
                                label={LABEL.emailAddress}
                                value={profile.emailAddress}
                            />
                        </Grid>
                    </Grid>
                )}
            </OverviewCard>
        </Box>
    );
}

function ConfirmationDialog({ open, title, message, busy, onClose, onConfirm }) {
    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            sx={{ zIndex: DIALOG_Z_INDEX }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{message}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>
                    {LABEL.close}
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    disabled={busy}
                    startIcon={busy
                        ? <CircularProgress size={18} color="inherit" />
                        : <CancelOutlinedIcon />}
                    onClick={() => {
                        void onConfirm();
                    }}
                >
                    {LABEL.confirm}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LeadSkeleton() {
    return (
        <Box sx={STYLE.page}>
            <Stack spacing={3}>
                <Skeleton
                    variant="rounded"
                    height={148}
                    sx={{ borderRadius: 3 }}
                />
                <Skeleton
                    variant="rounded"
                    height={130}
                    sx={{ borderRadius: 3 }}
                />
                <Grid container spacing={3}>
                    {[0, 1, 2, 3].map((index) => (
                        <Grid item xs={12} md={6} lg={3} key={index}>
                            <Skeleton
                                variant="rounded"
                                height={240}
                                sx={{ borderRadius: 3 }}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Stack>
        </Box>
    );
}

function CheckoutCard({ checkoutUrl, checkoutValidTo }) {
    const available = hasValue(checkoutUrl);

    return (
        <Box component="section" sx={STYLE.section}>
            <SectionHeading>{LABEL.payment}</SectionHeading>
            <OverviewCard
                title={LABEL.checkout}
                icon={<ShoppingCartOutlinedIcon fontSize="small" />}
            >
                <Stack spacing={2}>
                    <Typography color="text.secondary">
                        {available
                            ? MESSAGE.checkoutRequired
                            : MESSAGE.checkoutUnavailable}
                    </Typography>

                    {hasValue(checkoutValidTo) && (
                        <InfoRow
                            label={LABEL.paymentDeadline}
                            value={formatDateTime(checkoutValidTo)}
                        />
                    )}

                    <Box>
                        <Button
                            component={available ? "a" : "button"}
                            href={available ? checkoutUrl : undefined}
                            target={available ? "_blank" : undefined}
                            rel={available ? "noopener noreferrer" : undefined}
                            variant="contained"
                            disabled={!available}
                            startIcon={<ShoppingCartOutlinedIcon />}
                            sx={{
                                backgroundColor: SECONDARY_COLOR,
                                "&:hover": {
                                    backgroundColor: SECONDARY_COLOR,
                                    filter: "brightness(0.92)",
                                },
                            }}
                        >
                            {LABEL.startCheckout}
                        </Button>
                    </Box>
                </Stack>
            </OverviewCard>
        </Box>
    );
}

export function LeadDetails({ lead, showExclusiveInfo }) {
    const isB2B = lead?.type === "B2B";

    return (
        <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={6} lg={3}>
                <OverviewCard
                    title={LABEL.lead}
                    icon={<InfoOutlinedIcon fontSize="small" />}
                >
                    <Stack spacing={2.1}>
                        <InfoRow label={LABEL.id} value={lead.id} />
                        <InfoRow label={LABEL.category} value={getCategoryName(lead)} />
                        <InfoRow label={LABEL.type} value={getLeadTypeLabel(lead.type)} />
                        <InfoRow
                            label={LABEL.creationDate}
                            value={formatDateTime(lead.creationTimestamp)}
                        />
                    </Stack>
                </OverviewCard>
            </Grid>

            {showExclusiveInfo && (
                <>
                    <Grid item xs={12} md={6} lg={3}>
                        <OverviewCard
                            title={LABEL.person}
                            icon={<PersonOutlineIcon fontSize="small" />}
                        >
                            <Stack spacing={2.1}>
                                {isB2B && (
                                    <InfoRow
                                        label={LABEL.businessName}
                                        value={lead.businessName}
                                    />
                                )}
                                <InfoRow label={LABEL.firstName} value={lead.firstName} />
                                <InfoRow label={LABEL.lastName} value={lead.lastName} />
                                <InfoRow
                                    label={LABEL.birthDate}
                                    value={formatDate(lead.birthDate)}
                                />
                            </Stack>
                        </OverviewCard>
                    </Grid>

                    <Grid item xs={12} md={6} lg={3}>
                        <OverviewCard
                            title={LABEL.contact}
                            icon={<ContactMailOutlinedIcon fontSize="small" />}
                        >
                            <Stack spacing={2.1}>
                                <InfoRow
                                    label={LABEL.phoneNumber}
                                    value={lead.phoneNumber}
                                />
                                <InfoRow
                                    label={LABEL.emailAddress}
                                    value={lead.emailAddress}
                                />
                            </Stack>
                        </OverviewCard>
                    </Grid>

                    <Grid item xs={12} md={6} lg={3}>
                        <OverviewCard
                            title={LABEL.address}
                            icon={<HomeOutlinedIcon fontSize="small" />}
                        >
                            <Stack spacing={2.1}>
                                <InfoRow label={LABEL.street} value={lead.street} />
                                <InfoRow label={LABEL.houseNo} value={lead.houseNo} />
                                <InfoRow label={LABEL.zipCode} value={lead.zipCode} />
                                <InfoRow label={LABEL.town} value={lead.town} />
                                <InfoRow label={LABEL.country} value={lead.country} />
                            </Stack>
                        </OverviewCard>
                    </Grid>
                </>
            )}

            <Grid item xs={12}>
                <OverviewCard
                    title={LABEL.comments}
                    icon={<DescriptionOutlinedIcon fontSize="small" />}
                >
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={showExclusiveInfo ? 6 : 12}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: "block",
                                    mb: 0.75,
                                    fontWeight: 700,
                                }}
                            >
                                {LABEL.publicComment}
                            </Typography>
                            <Typography
                                sx={{
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {toEmptyStringIfNull(lead.publicComment).trim() === ""
                                    ? MESSAGE.noComment
                                    : toEmptyStringIfNull(lead.publicComment)
                                        .replaceAll("$", "\n")}
                            </Typography>
                        </Grid>

                        {showExclusiveInfo && (
                            <Grid item xs={12} md={6}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: "block",
                                        mb: 0.75,
                                        fontWeight: 700,
                                    }}
                                >
                                    {LABEL.privateComment}
                                </Typography>
                                <Typography
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {toEmptyStringIfNull(lead.privateComment).trim() === ""
                                        ? MESSAGE.noComment
                                        : toEmptyStringIfNull(lead.privateComment)
                                            .replaceAll("$", "\n")}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </OverviewCard>
            </Grid>
        </Grid>
    );
}

export default function Lead({ appAPI, contextAPI, leadId }) {
    const [lead, setLead] = useState(null);
    const [availableTags, setAvailableTags] = useState([]);
    const [auctionStatus, setAuctionStatus] = useState(null);
    const [saleNow, setSaleNow] = useState(Date.now());

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [busyAction, setBusyAction] = useState(null);

    const [noteText, setNoteText] = useState("");
    const [notesPage, setNotesPage] = useState(1);
    const [abortDialog, setAbortDialog] = useState(null);

    const [publicProfile, setPublicProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileFailed, setProfileFailed] = useState(false);

    const accessToken = contextAPI.accessToken ?? contextAPI.password;
    const userId = contextAPI.id;

    const showMessageRef = useRef(appAPI.showMessage);
    const leadRequestSequenceRef = useRef(0);
    const pollingErrorShownRef = useRef(false);

    useEffect(() => {
        showMessageRef.current = appAPI.showMessage;
    }, [appAPI.showMessage]);

    const loadLead = useCallback(async ({
        initial = false,
        notifyError = true,
        background = false,
    } = {}) => {
        if (
            leadId === null
            || leadId === undefined
            || typeof accessToken !== "string"
            || accessToken.trim() === ""
        ) {
            const error = new Error(MESSAGE.accessTokenMissing);

            if (initial) {
                setLead(null);
                setLoading(false);
                setLoadFailed(true);
            }

            if (notifyError) {
                showMessageRef.current(error.message);
            }

            throw error;
        }

        const requestSequence = ++leadRequestSequenceRef.current;

        if (initial) {
            setLoading(true);
            setRefreshing(false);
            setLoadFailed(false);
        } else if (!background) {
            setRefreshing(true);
        }

        try {
            const response = await readResponsePayload(
                fetchLeadAPI(accessToken, leadId, userId),
                MESSAGE.loadingError,
            );

            if (requestSequence !== leadRequestSequenceRef.current) {
                return null;
            }

            setLead(response);
            setLoadFailed(false);
            return response;
        } catch (error) {
            if (requestSequence === leadRequestSequenceRef.current) {
                if (initial) {
                    setLead(null);
                    setLoadFailed(true);
                }

                if (notifyError) {
                    showMessageRef.current(error?.message || MESSAGE.loadingError);
                }
            }

            throw error;
        } finally {
            if (requestSequence === leadRequestSequenceRef.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [accessToken, leadId, userId]);

    const synchronizeLeadUntil = useCallback(async (predicate) => {
        let lastLead = null;

        for (
            let attempt = 0;
            attempt < CONFIG.synchronizationAttempts;
            attempt += 1
        ) {
            try {
                lastLead = await loadLead({
                    initial: false,
                    notifyError: false,
                });

                if (lastLead && predicate(lastLead)) {
                    return lastLead;
                }
            } catch {
                // Während der kurzen Synchronisation nicht jeden Versuch melden.
            }

            if (attempt < CONFIG.synchronizationAttempts - 1) {
                await sleep(CONFIG.synchronizationIntervalMs);
            }
        }

        throw new Error(MESSAGE.synchronizationError);
    }, [loadLead]);

    useEffect(() => {
        setLead(null);
        setNoteText("");
        setNotesPage(1);
        setAuctionStatus(null);
        setAbortDialog(null);
        setPublicProfile(null);
        setProfileFailed(false);
        setSaleNow(Date.now());

        void loadLead({
            initial: true,
            notifyError: true,
        }).catch(() => {});
    }, [leadId, loadLead]);

    useEffect(() => {
        if (typeof accessToken !== "string" || accessToken.trim() === "") {
            return undefined;
        }

        let active = true;

        void (async () => {
            try {
                const response = await readResponsePayload(
                    fetchSaleTagsAPI(accessToken),
                    MESSAGE.tagsError,
                );

                if (active) {
                    setAvailableTags(Array.isArray(response) ? response : []);
                }
            } catch (error) {
                if (active) {
                    setAvailableTags([]);
                    showMessageRef.current(error?.message || MESSAGE.tagsError);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [accessToken]);

    const latestApprovedAgreement = useMemo(() => (
        getLatestApprovedPriceAgreement(lead?.priceAgreementList)
    ), [lead?.priceAgreementList]);

    const currentSaleSelection = useMemo(() => getCurrentSale(lead), [lead]);
    const currentSaleMode = currentSaleSelection.mode;
    const currentSale = currentSaleSelection.sale;

    const buyerId = latestApprovedAgreement?.buyerId ?? null;
    const isAuthor = idsAreEqual(userId, lead?.authorId);

    const paymentPending = lead?.status === LEAD_STATUS.soldNotPaid;
    const paymentCompleted = lead?.status === LEAD_STATUS.soldPaid;
    const hasBuyerCheckout = paymentPending && hasValue(lead?.checkoutUrl);

    // checkoutUrl wird vom Backend ausschließlich im BUYER_NOT_PAID-View
    // ausgeliefert. Damit bleibt der Checkout auch dann erreichbar, wenn die
    // Käufer-ID im PriceAgreement nicht verfügbar sein sollte.
    const isBuyer = idsAreEqual(userId, buyerId) || hasBuyerCheckout;
    const buyerHasPaid = isBuyer && paymentCompleted;
    const canViewExclusiveInfo = isAuthor || buyerHasPaid;
    const showCheckout = hasBuyerCheckout;

    const canViewInvoices = Boolean(
        latestApprovedAgreement
        && canViewExclusiveInfo,
    );
    const showNotes = buyerHasPaid;
    const showResell = isAuthor && isResellableLeadStatus(lead?.status);
    const accessRevokedForBuyer = Boolean(
        lead?.status === LEAD_STATUS.notPaidSaleReversed
        && !isAuthor,
    );

    const counterparty = useMemo(() => {
        if (isAuthor) {
            return {
                userId: buyerId,
                title: LABEL.buyerProfile,
            };
        }

        return {
            userId: lead?.authorId ?? null,
            title: LABEL.sellerProfile,
        };
    }, [buyerId, isAuthor, lead?.authorId]);

    useEffect(() => {
        setAuctionStatus(null);
        pollingErrorShownRef.current = false;
    }, [currentSale?.id, currentSaleMode]);

    useEffect(() => {
        if (!isSaleRunning(currentSale, saleNow)) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setSaleNow(Date.now());
        }, 1_000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [currentSale?.duration, currentSale?.id, currentSale?.startTimestamp, currentSale?.status]);

    const shouldPollCurrentSale = Boolean(
        currentSale
        && currentSaleMode
        && lead?.status === LEAD_STATUS.forSale
        && accessToken
        && userId !== null
        && userId !== undefined
        && isOpenSaleStatus(currentSale.status),
    );

    const salePollingRestartKey = JSON.stringify([
        accessToken,
        currentSale?.buyerId,
        currentSale?.duration,
        currentSale?.id,
        currentSale?.purchaseTimestamp,
        currentSale?.startTimestamp,
        currentSale?.status,
        currentSaleMode,
        userId,
    ]);

    usePolling({
        enabled: shouldPollCurrentSale,
        immediate: true,
        restartKey: salePollingRestartKey,
        getDelay: ({ pollingStartedAt }) => getSalePollingDelay({
            endTimestamp: getSaleEndTimestamp(currentSale),
            pollingStartedAt,
        }),
        poll: async ({ isPollingActive }) => {
            const saleId = currentSale.id;
            const endTimestamp = getSaleEndTimestamp(currentSale);

            const reloadAfterChange = async () => {
                try {
                    await loadLead({
                        initial: false,
                        notifyError: false,
                    });
                } catch (error) {
                    if (isPollingActive() && !pollingErrorShownRef.current) {
                        pollingErrorShownRef.current = true;
                        showMessageRef.current(
                            error?.message || MESSAGE.synchronizationError,
                        );
                    }
                }
            };

            try {
                const now = Date.now();
                const endedByTime = endTimestamp !== null && now >= endTimestamp;

                if (currentSaleMode === SALE_MODE.AUCTION) {
                    const status = await readResponsePayload(
                        fetchAuctionStatusAPI(accessToken, saleId, userId),
                        MESSAGE.auctionStatusError,
                    );

                    if (!isPollingActive()) {
                        return undefined;
                    }

                    const nextStatus = status?.auctionStatus ?? null;
                    const statusChanged = nextStatus !== null
                        && nextStatus !== currentSale.status;

                    if (
                        idsAreEqual(status?.auctionId, saleId)
                        && !statusChanged
                        && !endedByTime
                    ) {
                        setAuctionStatus(status);
                    }

                    if (statusChanged || endedByTime || isTerminalSaleStatus(nextStatus)) {
                        await reloadAfterChange();
                        setSaleNow(now);
                        return CONFIG.synchronizationIntervalMs;
                    }
                } else {
                    const offer = await readResponsePayload(
                        fetchOfferAPI(accessToken, saleId, userId),
                        MESSAGE.offerStatusError,
                    );

                    if (!isPollingActive()) {
                        return undefined;
                    }

                    const nextStatus = offer?.status ?? currentSale.status;
                    const nextPurchaseTimestamp = offer?.purchaseTimestamp ?? null;
                    const currentPurchaseTimestamp = currentSale.purchaseTimestamp ?? null;
                    const nextBuyerId = offer?.buyerId ?? null;
                    const currentBuyerId = currentSale.buyerId ?? null;
                    const statusChanged = nextStatus !== currentSale.status;
                    const purchaseChanged = nextPurchaseTimestamp
                        !== currentPurchaseTimestamp;
                    const buyerChanged = nextBuyerId !== currentBuyerId;

                    if (
                        statusChanged
                        || purchaseChanged
                        || buyerChanged
                        || endedByTime
                        || isTerminalSaleStatus(nextStatus)
                    ) {
                        await reloadAfterChange();
                        setSaleNow(now);
                        return CONFIG.synchronizationIntervalMs;
                    }
                }

                pollingErrorShownRef.current = false;
                setSaleNow(now);
                return undefined;
            } catch (error) {
                if (!isPollingActive()) {
                    return undefined;
                }

                if (!pollingErrorShownRef.current) {
                    pollingErrorShownRef.current = true;
                    showMessageRef.current(
                        error?.message
                        || (currentSaleMode === SALE_MODE.OFFER
                            ? MESSAGE.offerStatusError
                            : MESSAGE.auctionStatusError),
                    );
                }

                setSaleNow(Date.now());
                return undefined;
            }
        },
    });

    const shouldPollPendingPayment = Boolean(
        paymentPending
        && accessToken
        && userId !== null
        && userId !== undefined
        && leadId !== null
        && leadId !== undefined,
    );

    const paymentPollingRestartKey = JSON.stringify([
        accessToken,
        leadId,
        userId,
    ]);

    usePolling({
        enabled: shouldPollPendingPayment,
        speed: POLLING_SPEED.medium,
        restartKey: paymentPollingRestartKey,
        poll: async () => {
            await loadLead({
                initial: false,
                notifyError: false,
                background: true,
            });
        },
    });

    useEffect(() => {
        const profileUserId = counterparty.userId;

        if (
            !canViewExclusiveInfo
            || profileUserId === null
            || profileUserId === undefined
            || !accessToken
        ) {
            setPublicProfile(null);
            setProfileLoading(false);
            setProfileFailed(false);
            return undefined;
        }

        let active = true;

        setPublicProfile(null);
        setProfileLoading(true);
        setProfileFailed(false);

        void (async () => {
            try {
                const response = await readResponsePayload(
                    fetchUserProfileAPI(profileUserId, accessToken),
                    MESSAGE.profileError,
                );

                if (active) {
                    setPublicProfile(response);
                    setProfileLoading(false);
                }
            } catch (error) {
                if (active) {
                    setPublicProfile(null);
                    setProfileLoading(false);
                    setProfileFailed(true);
                    showMessageRef.current(error?.message || MESSAGE.profileError);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [accessToken, canViewExclusiveInfo, counterparty.userId]);

    useEffect(() => {
        if (!abortDialog) {
            return;
        }

        const auctionCanStillBeAborted = abortDialog.type !== SALE_MODE.AUCTION
            || (
                idsAreEqual(auctionStatus?.auctionId, currentSale?.id)
                && !hasValue(auctionStatus?.maxBid)
            );
        const stillMatches = currentSale
            && idsAreEqual(currentSale.id, abortDialog.id)
            && currentSaleMode === abortDialog.type
            && lead?.status === LEAD_STATUS.forSale
            && isSaleRunning(currentSale, saleNow)
            && auctionCanStillBeAborted;

        if (!stillMatches) {
            setAbortDialog(null);
        }
    }, [
        abortDialog,
        auctionStatus?.auctionId,
        auctionStatus?.maxBid,
        currentSale,
        currentSaleMode,
        lead?.status,
        saleNow,
    ]);

    const addNote = useCallback(async (event) => {
        event.preventDefault();

        const normalizedNote = noteText.trim();

        if (
            !lead
            || !showNotes
            || normalizedNote === ""
            || busyAction !== null
        ) {
            return;
        }

        setBusyAction(ACTION.addNote);

        try {
            await readResponsePayload(
                addLeadNoteAPI(accessToken, lead.id, normalizedNote),
                MESSAGE.noteError,
            );
            await loadLead({
                initial: false,
                notifyError: false,
            });
            setNoteText("");
            setNotesPage(1);
            showMessageRef.current(MESSAGE.noteSaved);
        } catch (error) {
            showMessageRef.current(error?.message || MESSAGE.noteError);
        } finally {
            setBusyAction(null);
        }
    }, [accessToken, busyAction, lead, loadLead, noteText, showNotes]);

    const createSale = useCallback(async ({ mode, price, tagIds }) => {
        if (
            !lead
            || !isAuthor
            || !isResellableLeadStatus(lead.status)
            || busyAction !== null
        ) {
            return;
        }

        setBusyAction(ACTION.resell);

        try {
            if (mode === SALE_MODE.OFFER) {
                await readResponsePayload(
                    resellLeadAPI(accessToken, lead.id, price, tagIds),
                    MESSAGE.offerCreateError,
                );
            } else {
                await readResponsePayload(
                    reauctionLeadAPI(accessToken, lead.id, price, tagIds),
                    MESSAGE.auctionCreateError,
                );
            }

            await synchronizeLeadUntil((refreshedLead) => {
                const selection = getCurrentSale(refreshedLead);

                return refreshedLead?.status === LEAD_STATUS.forSale
                    && selection.mode === mode
                    && selection.sale !== null;
            });

            setAuctionStatus(null);
            setSaleNow(Date.now());
            showMessageRef.current(
                mode === SALE_MODE.OFFER
                    ? MESSAGE.offerCreated
                    : MESSAGE.auctionCreated,
            );
        } catch (error) {
            showMessageRef.current(
                error?.message
                || (mode === SALE_MODE.OFFER
                    ? MESSAGE.offerCreateError
                    : MESSAGE.auctionCreateError),
            );
        } finally {
            setBusyAction(null);
        }
    }, [
        accessToken,
        busyAction,
        isAuthor,
        lead,
        synchronizeLeadUntil,
    ]);

    const confirmAbort = useCallback(async () => {
        if (
            !lead
            || !isAuthor
            || !abortDialog
            || busyAction !== null
        ) {
            return;
        }

        const isOffer = abortDialog.type === SALE_MODE.OFFER;
        const action = isOffer ? ACTION.abortOffer : ACTION.abortAuction;

        setBusyAction(action);

        try {
            if (isOffer) {
                await readResponsePayload(
                    abortOfferAPI(accessToken, abortDialog.id),
                    MESSAGE.offerAbortError,
                );
            } else {
                const latestAuctionStatus = await readResponsePayload(
                    fetchAuctionStatusAPI(
                        accessToken,
                        abortDialog.id,
                        userId,
                    ),
                    MESSAGE.auctionStatusError,
                );

                setAuctionStatus(latestAuctionStatus);

                if (
                    idsAreEqual(latestAuctionStatus?.auctionId, abortDialog.id)
                    && hasValue(latestAuctionStatus?.maxBid)
                ) {
                    setAbortDialog(null);
                    showMessageRef.current(MESSAGE.auctionAbortBidExists);
                    return;
                }

                await readResponsePayload(
                    abortAuctionAPI(accessToken, abortDialog.id),
                    MESSAGE.auctionAbortError,
                );
            }

            await synchronizeLeadUntil((refreshedLead) => {
                const selection = getCurrentSale(refreshedLead);

                if (refreshedLead?.status !== LEAD_STATUS.forSale) {
                    return true;
                }

                return !selection.sale
                    || !idsAreEqual(selection.sale.id, abortDialog.id)
                    || isTerminalSaleStatus(selection.sale.status);
            });

            setAbortDialog(null);
            setAuctionStatus(null);
            setSaleNow(Date.now());
            showMessageRef.current(
                isOffer ? MESSAGE.offerAborted : MESSAGE.auctionAborted,
            );
        } catch (error) {
            showMessageRef.current(
                error?.message
                || (isOffer
                    ? MESSAGE.offerAbortError
                    : MESSAGE.auctionAbortError),
            );
        } finally {
            setBusyAction(null);
        }
    }, [
        abortDialog,
        accessToken,
        busyAction,
        isAuthor,
        lead,
        synchronizeLeadUntil,
        userId,
    ]);

    const handleDocumentDownload = useCallback((value, fileName) => {
        try {
            downloadPdf(value, fileName);
        } catch (error) {
            showMessageRef.current(
                error?.message || MESSAGE.documentDownloadFailed,
            );
        }
    }, []);

    if (loading) {
        return <LeadSkeleton />;
    }

    if (loadFailed || !lead) {
        return (
            <Alert severity="error">
                {MESSAGE.loadingError}
            </Alert>
        );
    }

    if (currentSaleSelection.invalid) {
        return (
            <Alert severity="error">
                {MESSAGE.inconsistentSale}
            </Alert>
        );
    }

    if (accessRevokedForBuyer) {
        return (
            <Alert severity="error">
                {MESSAGE.reversedAccessDenied}
            </Alert>
        );
    }

    const statusConfig = getStatusConfig(lead.status, isAuthor, isBuyer);
    const saleModeConfig = getSaleModeConfig(currentSaleMode);

    return (
        <Box sx={STYLE.page}>
            <Stack spacing={4}>
                <Paper sx={STYLE.headerPaper}>
                    <Box sx={STYLE.headerBar}>
                        <Stack
                            direction={{
                                xs: "column",
                                md: "row",
                            }}
                            alignItems={{
                                xs: "flex-start",
                                md: "center",
                            }}
                            spacing={2.5}
                            sx={STYLE.headerContent}
                        >
                            <Avatar sx={STYLE.headerAvatar}>
                                {lead.type === "B2B"
                                    ? <BusinessOutlinedIcon />
                                    : <PersonOutlineIcon />}
                            </Avatar>

                            <Box
                                sx={{
                                    flexGrow: 1,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    component="h1"
                                    variant="h4"
                                    sx={STYLE.headerTitle}
                                >
                                    {getLeadHeading(lead)}
                                </Typography>

                                <Typography sx={STYLE.headerSubtitle}>
                                    Lead #{lead.id}
                                </Typography>

                                {saleModeConfig && (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        useFlexGap
                                        flexWrap="wrap"
                                        sx={{ marginTop: "16px" }}
                                    >
                                        <HeaderBadge
                                            icon={currentSaleMode === SALE_MODE.AUCTION
                                                ? <GavelOutlinedIcon />
                                                : <ShoppingCartOutlinedIcon />}
                                        >
                                            {saleModeConfig.label}
                                        </HeaderBadge>
                                    </Stack>
                                )}
                            </Box>

                            <Box sx={STYLE.badgeContainer}>
                                {refreshing && (
                                    <CircularProgress size={18} color="inherit" />
                                )}
                                {statusConfig && (
                                    <HeaderBadge>
                                        {statusConfig.label}
                                    </HeaderBadge>
                                )}
                            </Box>
                        </Stack>
                    </Box>
                </Paper>

                <PriceAgreementHighlight agreement={latestApprovedAgreement} />

                <Box component="section" sx={STYLE.section}>
                    <SectionHeading>{LABEL.details}</SectionHeading>
                    <LeadDetails
                        lead={lead}
                        showExclusiveInfo={canViewExclusiveInfo}
                    />
                </Box>

                {showCheckout && (
                    <CheckoutCard
                        checkoutUrl={lead.checkoutUrl}
                        checkoutValidTo={lead.checkoutValidTo}
                    />
                )}

                <Box component="section" sx={STYLE.section}>
                    <SectionHeading>{LABEL.currentSale}</SectionHeading>
                    <CurrentSaleCard
                        lead={lead}
                        saleMode={currentSaleMode}
                        sale={currentSale}
                        auctionStatus={auctionStatus}
                        saleNow={saleNow}
                        availableTags={availableTags}
                        isAuthor={isAuthor}
                        busyAction={busyAction}
                        onAbortOffer={() => {
                            setAbortDialog({
                                type: SALE_MODE.OFFER,
                                id: currentSale.id,
                            });
                        }}
                        onAbortAuction={() => {
                            setAbortDialog({
                                type: SALE_MODE.AUCTION,
                                id: currentSale.id,
                            });
                        }}
                    />
                </Box>

                <DocumentsSection
                    sale={currentSale}
                    agreement={latestApprovedAgreement}
                    leadId={lead.id}
                    canViewInvoices={canViewInvoices}
                    onDownload={handleDocumentDownload}
                />

                {canViewExclusiveInfo && (
                    <CounterpartyProfileSection
                        title={counterparty.title}
                        profile={publicProfile}
                        loading={profileLoading}
                        failed={profileFailed}
                        available={counterparty.userId !== null
                            && counterparty.userId !== undefined}
                    />
                )}

                {showResell && (
                    <Box component="section" sx={STYLE.section}>
                        <SectionHeading>{LABEL.resell}</SectionHeading>
                        <ResellCard
                            availableTags={availableTags}
                            busy={busyAction === ACTION.resell}
                            onCreate={(values) => {
                                void createSale(values);
                            }}
                        />
                    </Box>
                )}

                {showNotes && (
                    <NotesSection
                        notes={lead.notes}
                        noteText={noteText}
                        busy={busyAction === ACTION.addNote}
                        page={notesPage}
                        onPageChange={setNotesPage}
                        onNoteTextChange={setNoteText}
                        onSubmit={(event) => {
                            void addNote(event);
                        }}
                    />
                )}
            </Stack>

            <ConfirmationDialog
                open={abortDialog !== null}
                title={abortDialog?.type === SALE_MODE.OFFER
                    ? LABEL.abortOffer
                    : LABEL.abortAuction}
                message={abortDialog?.type === SALE_MODE.OFFER
                    ? MESSAGE.abortOfferQuestion
                    : MESSAGE.abortAuctionQuestion}
                busy={[
                    ACTION.abortOffer,
                    ACTION.abortAuction,
                ].includes(busyAction)}
                onClose={() => {
                    setAbortDialog(null);
                }}
                onConfirm={confirmAbort}
            />
        </Box>
    );
}